import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Button,
  Stack,
  Grid,
  Typography,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  FamilyRestroom as FamilyIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

const statusOptions = [
  'New', 'Active', 'Prospect', 'Qualified', 'Nurturing', 'Converted', 'Lost', 'Inactive',
];
const typeOptions = ['Buyer', 'Seller', 'Both', 'Other'];
const sourceOptions = ['Website', 'Referral', 'Social Media', 'Cold Call', 'Event', 'Advertisement', 'Other'];
const communicationOptions = ['Email', 'Phone', 'Text', 'In Person', 'Video Call'];

const VOICE_FIELD_MAP = {
  name: 'name',
  email: 'email',
  phone: 'phone',
  status: 'status',
  company: 'company',
  'job title': 'jobTitle',
  address: 'address',
  type: 'type',
  source: 'source',
  'lead score': 'leadScore',
  'preferred zip': 'preferredZip',
  'ai intent': 'aiIntent',
  website: 'website',
  linkedin: 'linkedin',
  twitter: 'twitter',
  birthday: 'birthday',
  spouse: 'spouse',
  children: 'children',
  budget: 'budget',
  timeline: 'timeline',
  communication: 'communication',
  'preferred communication': 'communication',
  'referred by': 'referredBy',
  stage: 'stage',
  interests: 'interests',
  language: 'language',
  timezone: 'timezone',
  tags: 'tags',
  notes: 'notes',
};

function ContactForm({
  contact = null, onSave, onCancel, loading = false, mode = 'add',
}) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', status: 'New', leadScore: '', preferredZip: '', aiIntent: '', company: '', address: '', tags: '', notes: '', avatar: '', jobTitle: '', website: '', linkedin: '', twitter: '', birthday: '', spouse: '', children: '', budget: '', timeline: '', communication: '', referredBy: '', stage: '', interests: '', language: '', timezone: '', type: 'Buyer', source: '',
  });
  const [errors, setErrors] = useState({});
  const [isListening, setIsListening] = useState(false);
  const [voiceInput, setVoiceInput] = useState('');
  const recognitionRef = useRef(null);
  const [useNlp, setUseNlp] = useState(true);
  const [provider, setProvider] = useState('ollama');
  useEffect(() => {
    if (contact) {
      setForm({ ...contact, tags: (contact.tags || []).join(', '), birthday: contact.birthday ? contact.birthday.split('T')[0] : '' });
    } else {
      setForm({
        name: '', email: '', phone: '', status: 'New', leadScore: '', preferredZip: '', aiIntent: '', company: '', address: '', tags: '', notes: '', avatar: '', jobTitle: '', website: '', linkedin: '', twitter: '', birthday: '', spouse: '', children: '', budget: '', timeline: '', communication: '', referredBy: '', stage: '', interests: '', language: '', timezone: '', type: 'Buyer', source: '',
      });
    }
    setErrors({});
  }, [contact]);
  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Email is invalid';
    if (form.leadScore && (form.leadScore < 0 || form.leadScore > 100)) newErrors.leadScore = 'Lead score must be between 0 and 100';
    if (form.website && !form.website.startsWith('http')) newErrors.website = 'Website must start with http:// or https://';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const formData = {
      ...form,
      tags: form.tags ? form.tags.split(',').map((tag) => tag.trim()).filter((tag) => tag) : [],
      leadScore: form.leadScore ? parseInt(form.leadScore) : null,
    };
    onSave(formData);
  };
  const handleVoicePrompt = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser.');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = async (event) => {
      const { transcript } = event.results[0][0];
      setVoiceInput(transcript);
      if (useNlp) {
        // NLP extraction via backend
        try {
          const res = await fetch('/api/v1/contacts/extract-fields', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ transcript, provider }),
          });
          const data = await res.json();
          if (data && typeof data === 'object') {
            setForm((prev) => ({ ...prev, ...data }));
          }
        } catch (err) {
          // fallback: show error or do nothing
        }
      } else {
        // Local mapping fallback
        const updates = {};
        const parts = transcript.toLowerCase().split(',').map((part) => part.trim());
        parts.forEach((part) => {
          Object.entries(VOICE_FIELD_MAP).forEach(([label, field]) => {
            if (part.startsWith(label)) {
              let value = part.replace(label, '').trim();
              if (field === 'tags') value = value.split(',').map((t) => t.trim()).filter(Boolean);
              updates[field] = value;
            }
          });
        });
        setForm((prev) => ({ ...prev, ...updates }));
      }
    };
    recognition.onerror = () => { setIsListening(false); setVoiceInput(''); };
    recognition.onend = () => { setIsListening(false); setVoiceInput(''); };
    recognition.start();
    recognitionRef.current = recognition;
    setIsListening(true);
  };
  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };
  return (
    <Box sx={{
      bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, p: { xs: 2, sm: 4 }, maxWidth: 900, mx: 'auto',
    }}
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <PersonIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight={700}>{mode === 'edit' ? 'Edit Contact' : 'Add New Contact'}</Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />
      </Stack>
      <form onSubmit={handleSave} style={{ width: '100%' }}>
        <Stack spacing={3}>
          {/* Voice Input Section */}
          <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Button variant={isListening ? 'outlined' : 'contained'} color={isListening ? 'error' : 'primary'} startIcon={isListening ? <StopIcon /> : <MicIcon />} onClick={handleVoicePrompt} size="small">{isListening ? 'Stop Voice Input' : 'Voice Input'}</Button>
              <FormControlLabel
                control={<Switch checked={useNlp} onChange={(e) => setUseNlp(e.target.checked)} color="primary" />}
                label="NLP Voice Input"
                sx={{ ml: 2 }}
              />
              {useNlp && (
                <FormControl size="small" sx={{ minWidth: 160, ml: 2 }}>
                  <InputLabel id="provider-label">Model</InputLabel>
                  <Select
                    labelId="provider-label"
                    value={provider}
                    label="Model"
                    onChange={(e) => setProvider(e.target.value)}
                  >
                    <MenuItem value="ollama">Ollama (Llama 3)</MenuItem>
                    <MenuItem value="llama4">Llama 4 (API)</MenuItem>
                    <MenuItem value="openai">OpenAI</MenuItem>
                  </Select>
                </FormControl>
              )}
              {voiceInput && (
              <Typography variant="body2" color="text.secondary">
                Voice input: "
                {voiceInput}
                "
              </Typography>
              )}
            </Stack>
          </Box>
          {/* Basic Information */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonIcon color="primary" />
                <Typography variant="h6">Basic Information</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField label="Name *" value={form.name} onChange={(e) => handleInputChange('name', e.target.value)} fullWidth error={!!errors.name} helperText={errors.name} /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Email *" value={form.email} onChange={(e) => handleInputChange('email', e.target.value)} fullWidth error={!!errors.email} helperText={errors.email} /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Phone" value={form.phone} onChange={(e) => handleInputChange('phone', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={form.status} onChange={(e) => handleInputChange('status', e.target.value)} label="Status">{statusOptions.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}</Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select value={form.type} onChange={(e) => handleInputChange('type', e.target.value)} label="Type">{typeOptions.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}</Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}><TextField label="Lead Score (0-100)" value={form.leadScore} onChange={(e) => handleInputChange('leadScore', e.target.value)} fullWidth type="number" inputProps={{ min: 0, max: 100 }} error={!!errors.leadScore} helperText={errors.leadScore} /></Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          {/* Professional Information */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <BusinessIcon color="primary" />
                <Typography variant="h6">Professional Information</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField label="Company" value={form.company} onChange={(e) => handleInputChange('company', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Job Title" value={form.jobTitle} onChange={(e) => handleInputChange('jobTitle', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Website" value={form.website} onChange={(e) => handleInputChange('website', e.target.value)} fullWidth error={!!errors.website} helperText={errors.website} /></Grid>
                <Grid item xs={12} sm={6}><TextField label="LinkedIn" value={form.linkedin} onChange={(e) => handleInputChange('linkedin', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Twitter" value={form.twitter} onChange={(e) => handleInputChange('twitter', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Address" value={form.address} onChange={(e) => handleInputChange('address', e.target.value)} fullWidth /></Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          {/* CRM Information */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <WorkIcon color="primary" />
                <Typography variant="h6">CRM Information</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Source</InputLabel>
                    <Select value={form.source} onChange={(e) => handleInputChange('source', e.target.value)} label="Source">{sourceOptions.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}</Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}><TextField label="Referred By" value={form.referredBy} onChange={(e) => handleInputChange('referredBy', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Preferred Zip Code" value={form.preferredZip} onChange={(e) => handleInputChange('preferredZip', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Budget" value={form.budget} onChange={(e) => handleInputChange('budget', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Timeline" value={form.timeline} onChange={(e) => handleInputChange('timeline', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Preferred Communication</InputLabel>
                    <Select value={form.communication} onChange={(e) => handleInputChange('communication', e.target.value)} label="Preferred Communication">{communicationOptions.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}</Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}><TextField label="Stage" value={form.stage} onChange={(e) => handleInputChange('stage', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Interests" value={form.interests} onChange={(e) => handleInputChange('interests', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12}><TextField label="AI Intent" value={form.aiIntent} onChange={(e) => handleInputChange('aiIntent', e.target.value)} fullWidth multiline rows={2} helperText="AI analysis of contact intent" /></Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          {/* Personal Information */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FamilyIcon color="primary" />
                <Typography variant="h6">Personal Information</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField label="Birthday" type="date" value={form.birthday} onChange={(e) => handleInputChange('birthday', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Spouse" value={form.spouse} onChange={(e) => handleInputChange('spouse', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Children" value={form.children} onChange={(e) => handleInputChange('children', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Language" value={form.language} onChange={(e) => handleInputChange('language', e.target.value)} fullWidth /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Timezone" value={form.timezone} onChange={(e) => handleInputChange('timezone', e.target.value)} fullWidth /></Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          {/* Additional Information */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SettingsIcon color="primary" />
                <Typography variant="h6">Additional Information</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}><TextField label="Tags (comma separated)" value={form.tags} onChange={(e) => handleInputChange('tags', e.target.value)} fullWidth helperText="Enter tags separated by commas" /></Grid>
                <Grid item xs={12}><TextField label="Notes" value={form.notes} onChange={(e) => handleInputChange('notes', e.target.value)} fullWidth multiline rows={4} /></Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          {/* Actions */}
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={onCancel} startIcon={<CancelIcon />}>Cancel</Button>
            <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={loading}>{loading ? 'Saving...' : (contact ? 'Update' : 'Save')}</Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
}

export default ContactForm;
